import React from 'react';
import {useTranslation} from 'react-i18next';
import {TscStyles, Glyphs, ControlButton, TscButtonType, TscThemeName} from '@techsmith/tsc-cloud-style-guide';
import {useTscTheme} from '../../context/themeProvider';
import styled from 'styled-components';
import config from '../../service/config';
import cssConstants from '../../constants/cssConstants';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import WhiteCheckmark16x16 from '../../static/svg/WhiteCheckmark16x16.svg';

const BoldText = styled.span`
   font-weight: ${TscStyles.font.weight.semibold};
   display: inline-flex;
   line-height: 1.25rem;
`;

const ModalBody = styled.div`
   padding: 1rem;
   text-align: left;
`;

const ModalFormField = styled.div`
   display: inline-block;
   margin-bottom: 1rem;
   line-height: normal;
`;

const EnhancedFeaturesMessageContainer = styled.span`
   display: inline-block;
   font-size: 0.875rem;
`;

const MessageContainer = styled.span`
   display: inline-block;
   line-height: 1.5rem;
`;

const CloseButtonField = styled.div`
   display: flex;
   justify-content: right;
`;

const UpgradeButtonField = styled.div`
   display: flex;
   justify-content: center;
`;

const UpgradeMessageContainer = styled.div`
   margin-bottom: 1rem;
   padding: 1rem;
   border-radius: ${TscStyles.border.radius.xxl};
   border: 1px solid ${TscStyles.color.message.caution};
`;

const CheckmarkContainer = styled.span`
   margin-right: 0.5rem;
`;

const FeatureList = styled.ul`
   font-size: 0.875rem;
   margin: 0 0 1rem 0;
   padding: 0 0 0 1.12rem;
   list-style: none;
`;

const FeatureListItem = styled.li` 
   margin-bottom: 0.5rem;
   line-height: normal;
`;

export const ProUpgradeModal: React.FC<IProUpgradeModalProps> = ({title, message, width, onClose}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [displayModal, setDisplayModal] = React.useState<boolean>(true);
   const handleClose = onClose || (() => setDisplayModal(false));

   const translationKeys = Object.keys(t('team.modal.proUpgrade.features', {returnObjects: true}));

   return (
      displayModal && (
         <BasicModalPortal
            testId="pro-upgrade-modal"
            visible={true}
            title={title}
            width={width || cssConstants.proUpgradeModalWidth}
            onClose={() => handleClose()}
            themeName={theme}
         >
            <ModalBody>
               <ModalFormField>
                  <MessageContainer>
                     {message}
                  </MessageContainer>
               </ModalFormField>

               <UpgradeMessageContainer className="form-body themeable-section">
                  <ModalFormField>
                     <BoldText>
                        {t('team.modal.proUpgrade.upgrade')}
                     </BoldText>
                  </ModalFormField>

                  <ModalFormField>
                     <EnhancedFeaturesMessageContainer>
                        {t('team.modal.proUpgrade.unlockFullPotential')}
                     </EnhancedFeaturesMessageContainer>
                  </ModalFormField>

                  <FeatureList>
                     {translationKeys.map((key, index) => (
                        <FeatureListItem key={index}>
                           <CheckmarkContainer>
                              {theme === TscThemeName.dawn ? <Glyphs.Checkmark16x16 /> : <WhiteCheckmark16x16 />}
                           </CheckmarkContainer>
                           {`${t(`team.modal.proUpgrade.features.${key}`)}`}
                        </FeatureListItem>
                     ))}
                  </FeatureList>

                  <UpgradeButtonField>
                     <ControlButton
                        label={t('team.buttons.upgradeToPro')}
                        buttonType={TscButtonType.primaryScreencast}
                        width="13.0625rem"
                        onClick={() => {
                           window.open(config.environmentData.ScreencastProPurchaseLinkUrl, '_blank');
                        }}
                     />
                  </UpgradeButtonField>
               </UpgradeMessageContainer>

               <CloseButtonField>
                  <ControlButton
                     label="Close"
                     buttonType={TscButtonType.secondary}
                     onClick={() => handleClose()}
                     themeName={theme}
                     width={cssConstants.mediumButtonWidth}
                     testId="close-button"
                  />
               </CloseButtonField>
            </ModalBody>
         </BasicModalPortal>
      )
   );
};

export interface IProUpgradeModalProps {
   title: string;
   message?: string;
   width?: string;
   onClose?(): void;
}

export default ProUpgradeModal;